import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Link } from "react-router-dom";
import { Cookie, ChevronDown, ChevronUp, Check } from "lucide-react";
import { Switch } from "./ui/switch";

const COOKIE_CATEGORIES = [
  {
    id: "essential",
    name: "Essential Cookies",
    description: "Required for the website to function. These cannot be disabled.",
    required: true,
    defaultEnabled: true,
  },
  {
    id: "analytics",
    name: "Analytics Cookies",
    description: "Help us understand how visitors interact with our website by collecting anonymous data.",
    required: false,
    defaultEnabled: false,
  },
  {
    id: "marketing",
    name: "Marketing Cookies",
    description: "Used to track visitors across websites to display relevant advertisements.",
    required: false,
    defaultEnabled: false,
  },
];

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState(() => {
    const saved = localStorage.getItem("cookie_preferences");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return null;
      }
    }
    return null;
  });

  useEffect(() => {
    // Check if consent was already given
    const hasConsent = localStorage.getItem("cookie_preferences");
    if (!hasConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Apply saved preferences
      try {
        const savedPrefs = JSON.parse(hasConsent);
        applyPreferences(savedPrefs);
      } catch (e) {
        console.error("Failed to parse cookie preferences", e);
      }
    }
  }, []);

  // Initialize preferences state when component mounts
  useEffect(() => {
    if (visible && !preferences) {
      const initialPrefs = {};
      COOKIE_CATEGORIES.forEach(cat => {
        initialPrefs[cat.id] = cat.defaultEnabled;
      });
      setPreferences(initialPrefs);
    }
  }, [visible, preferences]);

  const applyPreferences = (prefs) => {
    // Set global flags for tracking scripts
    window.cookieConsent = {
      essential: true, // Always enabled
      analytics: prefs.analytics || false,
      marketing: prefs.marketing || false,
    };
    
    // For legacy compatibility
    window.trackingEnabled = prefs.analytics || false;
    
    // Set cookies for server-side reading
    document.cookie = `cookie_consent=customized; path=/; max-age=31536000; SameSite=Lax`;
    document.cookie = `cookie_analytics=${prefs.analytics ? 'enabled' : 'disabled'}; path=/; max-age=31536000; SameSite=Lax`;
    document.cookie = `cookie_marketing=${prefs.marketing ? 'enabled' : 'disabled'}; path=/; max-age=31536000; SameSite=Lax`;
  };

  const savePreferences = (prefs) => {
    localStorage.setItem("cookie_preferences", JSON.stringify(prefs));
    applyPreferences(prefs);
    setVisible(false);
  };

  const handleAcceptAll = () => {
    const allEnabled = {};
    COOKIE_CATEGORIES.forEach(cat => {
      allEnabled[cat.id] = true;
    });
    savePreferences(allEnabled);
  };

  const handleDeclineAll = () => {
    const onlyEssential = {};
    COOKIE_CATEGORIES.forEach(cat => {
      onlyEssential[cat.id] = cat.required;
    });
    savePreferences(onlyEssential);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  const toggleCategory = (categoryId) => {
    setPreferences(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 p-4" data-testid="cookie-consent">
      <Card className="max-w-4xl mx-auto bg-card border-gold/30 shadow-lg">
        <CardContent className="p-4">
          {/* Main Banner */}
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <Cookie className="h-6 w-6 text-gold flex-shrink-0 mt-1" />
              <div className="flex-1">
                <p className="font-medium mb-1">We value your privacy</p>
                <p className="text-sm text-muted-foreground">
                  We use cookies to enhance your browsing experience, analyze site traffic, 
                  and personalize content. You can customize your preferences below or accept 
                  all cookies.{" "}
                  <Link to="/privacy" className="text-gold hover:underline">Privacy Policy</Link>
                </p>
              </div>
            </div>

            {/* Toggle Details Button */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-2 text-sm text-gold hover:text-gold/80 transition-colors w-fit"
              data-testid="cookie-customize-toggle"
            >
              {showDetails ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Hide cookie settings
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Customize cookie settings
                </>
              )}
            </button>

            {/* Detailed Cookie Settings */}
            {showDetails && preferences && (
              <div className="space-y-3 border-t border-border pt-4" data-testid="cookie-settings-panel">
                {COOKIE_CATEGORIES.map((category) => (
                  <div
                    key={category.id}
                    className="flex items-start justify-between gap-4 p-3 rounded-lg bg-background/50"
                    data-testid={`cookie-category-${category.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{category.name}</span>
                        {category.required && (
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                            Required
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{category.description}</p>
                    </div>
                    <Switch
                      checked={preferences[category.id]}
                      onCheckedChange={() => !category.required && toggleCategory(category.id)}
                      disabled={category.required}
                      data-testid={`cookie-toggle-${category.id}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              {showDetails ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeclineAll}
                    className="flex-1 sm:flex-none"
                    data-testid="cookie-decline-all"
                  >
                    Decline Non-Essential
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSavePreferences}
                    className="flex-1 sm:flex-none bg-gold text-primary-foreground hover:bg-gold/90"
                    data-testid="cookie-save-preferences"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Save Preferences
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAcceptAll}
                    className="flex-1 sm:flex-none bg-gold text-primary-foreground hover:bg-gold/90"
                    data-testid="cookie-accept-all"
                  >
                    Accept All
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeclineAll}
                    className="flex-1 sm:flex-none"
                    data-testid="cookie-decline"
                  >
                    Decline Non-Essential
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 sm:flex-none bg-gold text-primary-foreground hover:bg-gold/90"
                    onClick={handleAcceptAll}
                    data-testid="cookie-accept"
                  >
                    Accept All Cookies
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
